/**
 * Who may administer the app, and what that opens.
 *
 * This replaced a four-digit code compiled into the bundle. The code was an
 * honest gate for the one thing `/admin` used to hold - a `localStorage`
 * display preference - and no gate at all for anything since: feature flags for
 * the whole organisation, the audit log, and now people and groups. Those are
 * gated by permissions the server checks again on every request, which makes a
 * browser-side code worse than nothing: it implies a control that is not there.
 * Retiring it is step 7c-ii of `docs/plans/postgresql.md`.
 *
 * What is left is a question about the *navigation*: does this account have
 * administrative business at all, and should the account menu offer the page?
 * That is what `canAdminister` answers. Nothing here enforces anything - each
 * section asks for its own permission, and the API asks again.
 */

import { canDo, isRootUser } from './store.ts';

/**
 * Holding any one of these means there is something on Administration for you.
 *
 * Deliberately a set rather than a single `admin` role: the roles are built out
 * of permissions (migration `0003`), and a future role with only `audit:read`
 * should still find the page rather than be told it does not exist.
 */
export const ADMIN_PERMISSIONS = ['users:read', 'flags:read', 'audit:read'] as const;

/** Whether the account menu should offer Administration. */
export function canAdminister(): boolean {
  return ADMIN_PERMISSIONS.some((p) => canDo(p));
}

/**
 * The word for this account's standing, or `null` for an ordinary account.
 *
 * Root and admin are marked apart because they can do different things - root
 * alone grants and revokes `admin` - and a person who does not know which one
 * they are cannot know why a button is missing. That was the actual complaint
 * this came from: signed in, no idea whether the account was administrative.
 */
export function adminRole(): 'root' | 'admin' | null {
  if (isRootUser.value) return 'root';
  return canAdminister() ? 'admin' : null;
}

/**
 * Where the account menu should land: the first section this account can open.
 *
 * The rail would pick the same one on arrival, but naming it in the link means
 * a reload and a Back both keep the place, and the address bar says where you
 * are - which is how every other deep link in this app behaves.
 */
export function administrationHref(): string {
  if (canDo('users:read')) return '/admin#people';
  if (canDo('flags:read')) return '/admin#features';
  if (canDo('audit:read')) return '/admin#activity';
  return '/admin';
}
